import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Col,
    Row,
    Spinner,
    ButtonToolbar,
    OverlayTrigger,
    Tooltip,
    Button,
    Dropdown,
    Modal,
} from 'react-bootstrap';
import { getModalData, getProjectResumeTimesheets, sendMailProjectDocument } from '@lib/api';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import '@ag-grid-community/core/dist/styles/ag-grid.css';
import '@ag-grid-community/core/dist/styles/ag-theme-alpine.css';
import AG_GRID_LOCALE_IT from '@components/ag-grid/locale-it';
import OEFilterStaff from 'components/oefilter/OEFilterStaff';
import CustomPaginationMui from '@components/materialComponents/CustomPaginationMui';
import CustomQuickFilterBox from 'components/ag-grid/CustomQuickFilterBox';
import CustomTooltip from '@components/ag-grid/CustomTooltip';
import { formatToDecimals } from '@lib/helpers/formatting';
import { months } from '@lib/helpers/calendar';
import { IcoReportedPartially, IcoReported, IcoExclamationPoint, IcoCheck, IcoCheckFailed, IcoRefresh, ExcelIcon } from '@icons';
import { OETrans } from 'components/translation/OETrans';
import ProjectTimesheetModal from '@components/timesheet/projectTimesheetModal';
import OeApiResponseRenderer from 'components/OeApiResponseRenderer';
import { customCellStyle, columnToRight, columnCentered } from '@lib/helpers/agGridHelpers';

const InternalSupplies = ({ project, activeKey, isActiveSampling, sampling }) => {
    const [staff, setStaff] = useState([]);
    const [staffMeta, setStaffMeta] = useState({});
    const [columnDefsStaff, setColumnDefsStaff] = useState([]);
    const [modalData, setModalData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isFiltered, setIsFiltered] = useState(false);
    const [filterQueryParamsString, setFilterQueryParamsString] = useState('');
    const [isFirstResearch2, setIsFirstResearch2] = useState(true);
    const [modelFilterString, setModelFilterString] = useState('');
    const [apiToCall, setApiToCall] = useState('');
    const [show, setShow] = useState(false);
    const [spinnerVisible, setSpinnerVisible] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [isDownloadingSynthetic, setIsDownloadingSynthetic] = useState(false);
    const [isDownloadingAnalytical, setIsDownloadingAnalytical] = useState(false);
    const staffRef = useRef(null);
    const filter2Ref = useRef(null);
    const externalSuppliesRef = useRef(null);
    const localeText = AG_GRID_LOCALE_IT;
    const [sbShow, setSbShow] = useState(<></>);
    const [showModalSendReport, setShowModalSendReport] = useState(false);

    const handleStatusBar = (dataToHandle) => {
        setSbShow(<OeApiResponseRenderer dataToHandle={dataToHandle} />);
    };

    const fetchData = async (url) => {
        setSpinnerVisible(true);
        try {
            const response = await getModalData(url);
            setModalData(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        setSpinnerVisible(false);
    };

    const loadData = useCallback(() => {
        if (project.id) {
            getProjectResumeTimesheets(project.id, undefined, 'staff').then((retrievedResults) => {
                if (retrievedResults.success) {
                    setStaff(retrievedResults.data);
                    const { data, success, ...restResp } = retrievedResults;
                    setStaffMeta(restResp);
                }
            });
        }
    }, [project.id]);

    useEffect(() => {
        if (project.id && activeKey === 'intern-workers') {
            loadData();
        }
    }, [activeKey, loadData, project.id]);

    useEffect(() => {
        if (refresh) {
            loadData();

            setRefresh(false);
        }
    }, [loadData, refresh]);

    const renderCorrectModal = (modalToRender) => {
        switch (modalToRender) {
            case 'number':
                return <ProjectTimesheetModal
                    data={modalData}
                    setModalIsVisible={setShow}
                    modalIsVisible={show}
                    isSummary
                    spinnerVisible={spinnerVisible}
                    setModalData={setModalData}
                    setRefresh={setRefresh}
                    isProjectSco={project.sco}
                />
            default:
        }
    };

    const ReportedCheckRenderer = useCallback((params) => {
        const reporting = params?.data?.reporting;
        const reported = params?.data?.reported;
        const reportedTooltip = (props) => <Tooltip {...props}>Rendicontato</Tooltip>;
        const partialTooltip = (props) => <Tooltip {...props}>Parzialmente Rendicontato</Tooltip>;
        return (
            <>
                {reporting && (
                    <OverlayTrigger placement="top" overlay={partialTooltip}>
                        <Row className="align-items-center justify-content-center h-100">
                            <Col className="col-auto text-center">
                                <IcoReportedPartially />
                            </Col>
                        </Row>
                    </OverlayTrigger>
                )}
                {reported && !reporting && (
                    <OverlayTrigger placement="top" overlay={reportedTooltip}>
                        <Row className="align-items-center justify-content-center h-100 px-0">
                            <Col className="col-auto text-center">
                                <IcoReported />
                            </Col>
                        </Row>
                    </OverlayTrigger>
                )}
            </>
        );
    }, []);

    const TotalResumeChecklistCellRenderer = useCallback((params) => {
        //console.log('params', params);

        const red = '#C94723';
        const green = '#689689';
        const yellow = '#D4BE31';
        let stoplightColor = green;
        let isRendicontable = false;
        let isStoplightYellow = false;

        if (params.value && params.data) {
            const arrayEntities = ['tender', 'frameworkContract', 'contract', 'invoice', 'payment', 'timesheet'];

            arrayEntities.forEach((entity) => {
                if (params.data[entity]?.checkliststatus && params.data[entity]?.checkliststatus.color === 'green_white') {
                    stoplightColor = yellow;
                    isStoplightYellow = true;
                }
            });

            isRendicontable = true;
        } else {
            stoplightColor = red;
            isRendicontable = false;
        }

        const reportableTooltip = (props) => (
            <Tooltip {...props}>{isRendicontable ? (isStoplightYellow ? 'Eleggibile - da validare' : 'Eleggibile') : 'Non eleggibile'}</Tooltip>
        );

        return (
            <OverlayTrigger placement="top" overlay={reportableTooltip}>
                <Row className="align-items-center justify-content-center h-100">
                    <Col className="col-auto text-center">
                        {isRendicontable ? <IcoCheck style={{ fill: stoplightColor }} /> : <IcoCheckFailed style={{ fill: stoplightColor }} />}
                    </Col>
                </Row>
            </OverlayTrigger>
        );
    }, []);

    const ResumeChecklistCellRenderer = useCallback(
        (params) => {
            const stoplightColor = {
                red: '#C94723',
                yellow: '#D4BE31',
                green: '#689689',
                white: '#ffffff',
                gray: '#C4C4C4',
                grey: '#C4C4C4',
                green_white_settled: '#689689',
            };

            if (params.value && params?.value.keyValue) {
                let circleStyle;
                let message = '';

                if (!params.value.checkliststatus) {
                    circleStyle = {
                        display: 'inherit',
                        width: '1.2em',
                        height: '1.2em',
                        borderRadius: '2em',
                        MozBorderRadius: '2em',
                        WebkitBorderRadius: '2em',
                        backgroundColor: '#ffffff',
                        border: `2px solid ${'#C4C4C4'}`,
                    };

                    message = 'Da lavorare';
                } else {
                    let checklistColor = params.value.checkliststatus.color;

                    if (checklistColor === 'green_white' || checklistColor === 'green_white_settled') {
                        circleStyle = {
                            display: 'inherit',
                            width: '23px',
                            height: '23px',
                            border: '4px solid #689689',
                            borderRadius: '50%',
                            padding: '0px',
                            background: checklistColor === 'green_white_settled' ? 'linear-gradient(to right, #689689 50%, transparent 0)' : 'white',
                            boxSizing: 'border-box',
                        };

                        message = checklistColor === 'green_white_settled' ? 'Conforme - validato' : 'Conforme - da validare';
                    } else {
                        circleStyle = {
                            display: 'inherit',
                            width: '1.2em',
                            height: '1.2em',
                            borderRadius: '2em',
                            MozBorderRadius: '2em',
                            WebkitBorderRadius: '2em',
                            backgroundColor: stoplightColor[checklistColor],
                            border: `2px solid ${checklistColor === 'white' ? stoplightColor.grey : stoplightColor[checklistColor]}`,
                        };

                        switch (checklistColor) {
                            case 'white':
                                message = 'Da lavorare';
                                break;
                            case 'grey':
                            case 'gray':
                                message = 'In lavorazione';
                                break;
                            case 'red':
                                message = 'Non conforme';
                                break;
                            case 'yellow':
                                message = 'Incompleta';
                                break;
                            case 'green':
                                message = 'Conforme';
                                break;
                            default:
                                break;
                        }
                    }
                }

                if (
                    params?.queryParam === 'tender_number' &&
                    (params.value.key.keyValue?.startsWith('GARATEMP_') || params.value.key.keyValue?.startsWith('GARA_'))
                ) {
                    let circleColor = params.value.key.keyValue?.startsWith('GARATEMP_')
                        ? 'yellow'
                        : params.value.key.keyValue?.startsWith('GARA_')
                            ? 'grey'
                            : params.value?.checkliststatus?.color;

                    circleStyle = {
                        display: 'inherit',
                        width: '1.2em',
                        height: '1.2em',
                        borderRadius: '2em',
                        MozBorderRadius: '2em',
                        WebkitBorderRadius: '2em',
                        backgroundColor: stoplightColor[circleColor],
                        border: `2px solid ${stoplightColor[circleColor]}`,
                    };

                    switch (circleColor) {
                        case 'grey':
                        case 'gray':
                            message = 'Non applicabile';
                            break;
                        case 'yellow':
                            message = 'Non ancora disponibile';
                            break;
                        default:
                            break;
                    }
                }

                let value;

                if (params.value.key === 'tender_number' && params.value.keyValue.startsWith('GARA_')) {
                    value = 'N.A.';
                } else if (params.value.key === 'tender_number' && params.value.keyValue.startsWith('GARATEMP_')) {
                    value = 'N.D.';
                } else {
                    value = params.value.keyValue;
                }

                const canBeLink = () => {
                    if (project.streamConfigurationType === 'STREAM') {
                        return true;
                    }

                    return false;
                };

                const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

                return (
                    <>
                        <Row
                            className="align-items-center justify-content-between h-100 flex-nowrap"
                        >
                            {isActiveSampling && (
                                <>
                                    <Col className={`col-auto ${params?.queryParam === 'rp_code' && params?.data?.postReportedCheck ? 'pe-0' : ''}`}>
                                        <OverlayTrigger placement="top" overlay={<Tooltip>{message}</Tooltip>}>
                                            <i style={{ ...circleStyle }} />
                                        </OverlayTrigger>
                                    </Col>
                                    <Col className="col-auto px-0">
                                        {params?.queryParam === 'rp_code' && params?.data?.postReportedCheck ? (
                                            <IcoExclamationPoint style={{ verticalAlign: 'sub' }} />
                                        ) : (
                                            <span className={params?.queryParam === 'rp_code' && !params?.data?.postReportedCheck ? 'pe-1' : ''}>&nbsp;</span>
                                        )}
                                    </Col>
                                </>
                            )}
                            <Col className={isActiveSampling ? 'overflow-hidden ps-0' : ''}>
                                <ConditionalWrapper
                                    condition={params.link && !params.value.keyValue.startsWith('GARA_' || 'GARATEMP_') && canBeLink()}
                                    wrapper={(children) => (
                                        // TODO LOGIC TO OPEN MODAL
                                        <Button
                                            variant="link"
                                            className="d-flex align-items-start projectModalRenderBtn"
                                            style={{ color: '#2A3A64' }}
                                            onClick={() => {
                                                setApiToCall(params.value.key);
                                                fetchData(params.value.href.url);
                                                params.setShow(true);
                                            }}
                                        >
                                            {children}
                                        </Button>
                                    )}
                                >
                                    <span>
                                        {params.value.keyValue ? (
                                            typeof params.value.keyValue === 'number' ? (
                                                formatToDecimals(params.data.taxableAmount) + ' €'
                                            ) : (
                                                value
                                            )
                                        ) : (
                                            <>&zwnj;</>
                                        )}
                                    </span>
                                </ConditionalWrapper>
                            </Col>
                        </Row>
                    </>
                );
            }
        },
        [project.streamConfigurationType, isActiveSampling],
    );

    const defaultColDef = {
        resizable: true,
        // wrapText: true,
        // autoHeight: true,
        flex: 1,
        suppressMovable: true,
        //suppressSizeToFit: false,
        //suppressColumnVirtualisation=true
        cellStyle: {
            padding: '0 1rem',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
        },
        sortable: true,
        filterParams: {
            suppressAndOrCondition: true, //for disable AND/OR login in filter
            filterOptions: ['contains'],
        },
        suppressCellFocus: false,
        suppressHeaderFocus: false,
    };

    useEffect(() => {
        setColumnDefsStaff([
            {
                headerName: '',
                headerTooltip: 'Rendicontato',
                tooltipComponent: CustomTooltip,
                field: 'reported',
                cellRenderer: ReportedCheckRenderer,
                cellRendererParams: {
                    queryParam: 'reported',
                },
                minWidth: 70,
                maxWidth: 70,
                //flex: 1,
                resizable: false,
                ...columnCentered(),
            },
            {
                headerName: '',
                headerTooltip: 'Eleggibile',
                tooltipComponent: CustomTooltip,
                field: 'reportable',
                cellRenderer: TotalResumeChecklistCellRenderer,
                cellRendererParams: {
                    queryParam: 'reportable',
                },
                minWidth: 70,
                maxWidth: 70,
                //flex: 1,
                ...columnCentered(),
            },
            {
                headerName: 'Dipendente',
                field: 'employee.keyValue',
                filter: 'agTextColumnFilter',
                cellStyle: { ...customCellStyle },
                cellRendererParams: {
                    queryParam: 'serial_number',
                },
                flex: 1,
            },
            {
                headerName: 'Anno',
                field: 'year',
                headerClass: 'text-center',
                cellRendererParams: {
                    queryParam: 'year',
                },
                cellStyle: { ...customCellStyle, justifyContent: 'center' },
                flex: 1,
                maxWidth: 100,
            },
            {
                headerName: 'Mese',
                field: 'month',
                headerClass: 'text-center',
                cellStyle: { ...customCellStyle, justifyContent: 'center' },
                valueGetter: (params) => {
                    return months()[params.data.month - 1];
                },
                cellRendererParams: {
                    queryParam: 'month',
                },
                flex: 1,
            },
            {
                headerName: 'Categoria',
                field: 'category',
                filter: 'agTextColumnFilter',
                cellStyle: {
                    ...customCellStyle,
                },
                cellRendererParams: {
                    queryParam: 'category',
                },
                flex: 1,
            },
            {
                headerName: 'Record',
                field: 'timesheet',
                cellRenderer: ResumeChecklistCellRenderer,
                cellRendererParams: {
                    link: true,
                    setShow: setShow,
                    setApiToCall: setApiToCall,
                    queryParam: 'number',
                },
                minWidth: 230,
                filter: 'agTextColumnFilter',
                filterValueGetter: (params) => {
                    return params.data.timesheet.keyValue;
                },
                comparator: (valueA, valueB) => {
                    if (valueA.keyValue === valueB.keyValue) return 0;
                    return valueA.keyValue > valueB.keyValue ? 1 : -1;
                },
                flex: 1,
            },
            {
                headerName: 'Beneficiario',
                field: 'beneficiary.name',
                minWidth: 170,
                filter: 'agTextColumnFilter',
                filterValueGetter: (params) => {
                    return params.data.beneficiary.name;
                },
            },
            {
                headerName: 'Costo totale €',
                field: 'totalCost',
                valueFormatter: (params) => {
                    return formatToDecimals(params.data.totalCost);
                },
                cellRendererParams: {
                    queryParam: 'total_cost',
                    typeOf: 'number',
                },
                filter: 'agTextColumnFilter',
                filterParams: {
                    filterOptions: ['equals'],
                    textMatcher: ({ filterOption, value, filterText }) => {
                        if (filterText == null) return false;

                        switch (filterOption) {
                            case 'equals':
                                return value === filterText || formatToDecimals(Number(value)) === filterText;
                            default:
                                return false;
                        }
                    },
                },
                comparator: (_valueA, _valueB, nodeA, nodeB) => {
                    if (nodeA.data.totalCost === nodeB.data.totalCost) return 0;
                    return nodeA.data.totalCost > nodeB.data.totalCost ? 1 : -1;
                },
                flex: 1,
                minWidth: 170,
                ...columnToRight(),
            },
        ]);
    }, [ReportedCheckRenderer, ResumeChecklistCellRenderer, TotalResumeChecklistCellRenderer]);

    const handleClick = (ref) => {
        ref.current?.handleReset();
    };

    const onFilterChanged = (e, reset) => {
        //console.log('log1 e:', e);

        if (!reset) {
            const model = e.api.getFilterModel();

            let queryParamsString = '';

            for (const [key, value] of Object.entries(model)) {
                let keyFormatted = key;

                switch (key) {
                    case 'tender':
                        keyFormatted = 'tender_number';
                        break;
                    case 'frameworkContract':
                        keyFormatted = 'framework_contract_number';
                        break;
                    case 'contract':
                        keyFormatted = 'contract_number';
                        break;
                    case 'invoice':
                        keyFormatted = 'rp_code';
                        break;
                    case 'employee.keyValue':
                        keyFormatted = 'serial_number';
                        break;
                    case 'timesheet':
                        keyFormatted = 'number';
                        break;
                    case 'totalCost':
                        keyFormatted = 'total_cost';
                        break;
                    default:
                        break;
                }

                queryParamsString += `${queryParamsString === '' ? '?' : '&'}${keyFormatted}=${value.filter}`;
            }

            if (setModelFilterString) setModelFilterString(queryParamsString);
        } else {
            e?.api?.setFilterModel(null);

            if (setModelFilterString) setModelFilterString('');
        }
    };

    const handleFileDownload = async (projectId, resumeSection, setIsDownloadingButton) => {
        setSbShow(<></>);

        setIsDownloadingButton(true);

        let filterString = isFiltered ? filterQueryParamsString : modelFilterString;

        const result = await sendMailProjectDocument(projectId, 'resume' + resumeSection, filterString);

        if (!result.success) {
            handleStatusBar(result);

            if (activeKey === 'external-supplies') {
                onFilterChanged(externalSuppliesRef, true);
            } else if (activeKey === 'intern-workers') {
                onFilterChanged(staffRef, true);
            }
        }

        setIsDownloadingButton(false);
        setShowModalSendReport(true);
    };

    const downloadExcelButtons = (resumeSection, fileName) => {
        return (
            <>
                <Dropdown className="me-2" autoClose="outside">
                    <Dropdown.Toggle id="dropdown-autoclose-outside" style={{ padding: '0.25rem 0.4rem' }}>
                        <ExcelIcon style={{ width: '24px', height: '24px' }} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Header>Scarica il riepilogo dei Costi esterni</Dropdown.Header>
                        <Dropdown.Item
                            as="button"
                            className="d-flex align-items-center"
                            onClick={() => {
                                handleFileDownload(project.id, resumeSection, setIsDownloadingSynthetic);
                            }}
                        >
                            Riepilogo sintetico
                            {isDownloadingSynthetic && (
                                <Spinner
                                    style={{
                                        height: '20px',
                                        width: '20px',
                                        marginLeft: '0.5rem',
                                    }}
                                    animation="border"
                                    size="md"
                                    role="status"
                                >
                                    <span style={{ visibility: 'hidden' }}>
                                        <OETrans code="common.loading" fallback={'Caricamento'} />
                                        ...
                                    </span>
                                </Spinner>
                            )}
                        </Dropdown.Item>
                        <Dropdown.Item
                            as="button"
                            className="d-flex align-items-center"
                            onClick={() => {
                                handleFileDownload(project.id, 'analytic' + resumeSection, setIsDownloadingAnalytical);
                            }}
                        >
                            Riepilogo analitico
                            {isDownloadingAnalytical && (
                                <Spinner
                                    style={{
                                        height: '20px',
                                        width: '20px',
                                        marginLeft: '0.5rem',
                                    }}
                                    animation="border"
                                    size="md"
                                    role="status"
                                >
                                    <span style={{ visibility: 'hidden' }}>
                                        <OETrans code="common.loading" fallback={'Caricamento'} />
                                        ...
                                    </span>
                                </Spinner>
                            )}
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </>
        );
    };

    const refreshButton = () => {
        return (
            <OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
                <Button
                    onClick={() => {
                        setIsLoading(true);
                        setTimeout(() => {
                            setRefresh(true);
                        }, 1000);
                    }}
                    className="me-2"
                    style={{ padding: '0.2rem', height: '2.235rem', width: '2.211rem' }}
                >
                    {isLoading ? (
                        <Spinner
                            as="span"
                            className="mx-1"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            style={{ verticalAlign: 'middle' }}
                        />
                    ) : (
                        <IcoRefresh />
                    )}
                </Button>
            </OverlayTrigger>
        );
    };

    return (<>
        <Row className="align-items-center mb-3">
            <Col className="col-4"></Col>
            <Col className="col-8 text-right py-1">
                <ButtonToolbar className="justify-content-end">
                    {downloadExcelButtons('timesheets', `Personale-Interno-${project?.name}-${project?.cup}`)}

                    <OEFilterStaff
                        columnDefs={columnDefsStaff}
                        projectId={project.id}
                        setTable={setStaff}
                        setTableMeta={setStaffMeta}
                        isFiltered={isFiltered}
                        setIsFiltered={setIsFiltered}
                        //setFilteredTotal={setFilteredTotal}
                        handleClick={() => handleClick(filter2Ref)}
                        setFilterQueryParamsString={setFilterQueryParamsString}
                        tableRef={staffRef}
                        ref={filter2Ref}
                    />
                    {refreshButton()}
                    <CustomQuickFilterBox
                        tableRef={staffRef}
                        dataMeta={staffMeta}
                        setData={setStaff}
                        dataLength={staff?.length}
                        isFirstResearch={isFirstResearch2}
                        setIsFirstResearch={setIsFirstResearch2}
                        refTable="staff"
                        isRefreshing={isLoading}
                        setIsRefreshing={setIsLoading}
                    />
                </ButtonToolbar>
            </Col>
        </Row>

        <>{sbShow}</>

        {Array.isArray(staff) ? (
            <Row className="mb-5">
                <Col className="col-12 col-xxl-9" id="grid-wrapper">
                    {Object.keys(staffMeta).length > 0 ? (
                        <div className="ag-theme-alpine list-table" style={{ height: '800px' }}>
                            <AgGridReact
                                ref={staffRef}
                                columnDefs={columnDefsStaff}
                                rowData={staff}
                                defaultColDef={defaultColDef}
                                tooltipShowDelay={0}
                                tooltipHideDelay={2000}
                                tooltipMouseTrack={true}
                                // onGridSizeChanged={() => sizeToFit(staffRef)}
                                // onFirstDataRendered={() => onFirstDataRendered(staffRef)}
                                onFilterChanged={onFilterChanged}
                                headerHeight={50}
                                rowHeight={50}
                                ariaDescription="ag-grid-table"
                                suppressCellFocus={true}
                                autoSizeStrategy={{ type: 'fitCellContents' }}
                                rowSelection="single"
                                localeText={localeText}
                            />
                            {Object.keys(staffMeta).length > 0 && (
                                <CustomPaginationMui
                                    dataMeta={staffMeta}
                                    setData={setStaff}
                                    setDataMeta={setStaffMeta}
                                    refTable="staff"
                                    setIsFirstResearch={setIsFirstResearch2}
                                />
                            )}
                        </div>
                    ) : (
                        <Row className="text-center my-5">
                            <span>Non sono presenti elementi caricati</span>
                        </Row>
                    )}
                </Col>
            </Row>
        ) : (
            <div className="text-center my-4">
                <Spinner animation="border" role="status">
                    <span style={{ visibility: 'hidden' }}>Caricamento...</span>
                </Spinner>
            </div>
        )}

        {project && activeKey === 'intern-workers' ? renderCorrectModal(apiToCall) : null}

        <Modal show={showModalSendReport} onHide={() => setShowModalSendReport(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <OETrans code="common.warning" fallback={'Attenzione'} />
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <OETrans
                    code="common.warning.sendingmailreport"
                    fallback={
                        'Il report è in fase di elaborazione. Riceverai una email con il link per il download direttamente nella tua mail.'
                    }
                />
            </Modal.Body>
            <Modal.Footer />
        </Modal>
    </>);
};
export default InternalSupplies;